import { useEffect, useState } from 'react'
import { FirebaseError } from 'firebase/app'
import { useAuth } from '../lib/auth'
import { signInWithGoogle } from '../lib/firebase'
import { signInWithSightHub } from '../lib/sso'
import { Button, Callout, cx } from './ui'

/** What each way a SightHub round trip can fail means to the person it failed for. */
const SSO_FAILURES: Record<string, string> = {
  access_denied: 'access was denied',
  inactive: 'your wiki account is deactivated',
  identity: 'SightHub did not confirm who you are',
  firebase: 'the wiki could not start your session',
  store: 'the wiki could not save your account',
  no_code: 'SightHub sent no sign-in code back',
  bad_code: 'the sign-in code expired or was already used',
  network: 'the wiki server did not answer',
}

/** The code stays visible next to the words: it is what an admin will ask for. */
const ssoFailure = (reason: string) => {
  const gloss = SSO_FAILURES[reason]
  return gloss ? `${gloss} (${reason})` : reason
}

/** Closing Google's window is a change of mind, not a failure. */
const CANCELLED = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
])

/**
 * The two ways in. SightHub is the employee's AD account and opens internal docs;
 * Google is anyone's, for commenting, voting and saving pages.
 */
export function SignInChoice({
  autoFocus,
  onSignedIn,
  className,
}: {
  autoFocus?: boolean
  onSignedIn?: () => void
  className?: string
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const google = async () => {
    setBusy(true)
    setError(null)
    try {
      await signInWithGoogle()
      onSignedIn?.()
    } catch (e) {
      if (!(e instanceof FirebaseError && CANCELLED.has(e.code))) {
        setError(`Google sign-in failed: ${e instanceof FirebaseError ? e.code : String(e)}`)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={cx('flex flex-col gap-2', className)}>
      <Button autoFocus={autoFocus} onClick={signInWithSightHub} className="w-full">
        <img src="/sighthub-logo.png" alt="" width={16} height={16} className="size-4 object-contain" />
        Sign in with SightHub
      </Button>
      <Button onClick={() => void google()} disabled={busy} className="w-full">
        <GoogleIcon />
        Sign in with Google
      </Button>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  )
}

/**
 * The sign-in screen behind every "Sign in". A SightHub sign-in that came back failed
 * opens it on its own, so the reason sits right above both ways to try again.
 */
export function SignInDialog() {
  const { chooserOpen, ssoError, closeChooser } = useAuth()

  useEffect(() => {
    if (!chooserOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeChooser()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [chooserOpen, closeChooser])

  if (!chooserOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/40 px-4 pt-[15vh] backdrop-blur-sm"
      onClick={closeChooser}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-in-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-xl border border-line bg-surface p-6 shadow-pop"
      >
        <h2 id="sign-in-title" className="text-lg font-semibold text-ink">
          Sign in to SightHub Wiki
        </h2>
        <p className="mt-1.5 text-sm text-muted">
          Staff, use your SightHub account — it opens internal docs. Anyone can sign in with
          Google to comment, vote and save pages.
        </p>

        {ssoError && (
          <div className="mt-4" role="alert">
            <Callout tone="danger">SightHub sign-in failed: {ssoFailure(ssoError)}</Callout>
          </div>
        )}

        <SignInChoice autoFocus className="mt-5" onSignedIn={closeChooser} />
      </div>
    </div>
  )
}

const GoogleIcon = () => (
  <svg viewBox="0 0 18 18" className="size-4" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" />
    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
  </svg>
)
